import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, inputCls } from "@/components/common";

const when = (v: string) => (v && v !== "baseline" ? new Date(v).toLocaleString() : "");

function TestSend({ rings }: { rings: any[] }) {
  const { user } = useAuth();
  const [pid, setPid] = useState("");
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/admin-x/edition-alerts/test", { product_id: pid || rings[0]?.id, email }); toast(`Test email sent to ${email}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid sm:grid-cols-[2fr_2fr_auto] gap-2 items-end" data-testid="edition-alert-test">
      <Field label="Preview with ring">
        <select value={pid} onChange={(e) => setPid(e.target.value)} className={inputCls} data-testid="edition-alert-test-ring">
          {rings.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </Field>
      <Field label="Send test to"><input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="edition-alert-test-email" /></Field>
      <button disabled={busy || !email || !rings.length} onClick={send} className="btn-ink px-5 py-3 text-[11px] tracking-widest uppercase disabled:opacity-40" data-testid="edition-alert-test-send">{busy ? "Sending…" : "Send test"}</button>
    </div>
  );
}

export function EditionAlertsEditor({ s, set }: { s: any; set: (k: string, v: any) => void }) {
  const [st, setSt] = useState<any>(null);
  const load = () => api.get("/admin-x/edition-alerts").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    try { const r = await api.post("/admin-x/edition-alerts/run"); toast(r.data.pending ? `Sending ${r.data.pending} alert(s) now` : "No new edition rings waiting"); setTimeout(load, 4000); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="border border-neutral-200 p-4 space-y-4" data-testid="edition-alert-settings">
      <label className="flex items-center gap-3 text-sm"><input type="checkbox" checked={s.edition_alerts_enabled !== false} onChange={(e) => set("edition_alerts_enabled", e.target.checked)} data-testid="edition-alerts-enabled" />
        Email every subscriber when a new Limited or Atelier Edition ring arrives from the lookbook (one email per ring)</label>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Subject ({ring} and {edition} are filled in)"><input value={s.edition_alert_subject || ""} onChange={(e) => set("edition_alert_subject", e.target.value)} placeholder="Just released: {ring} — {edition}" className={inputCls} data-testid="edition-alert-subject-input" /></Field>
        <Field label="Message"><input value={s.edition_alert_text || ""} onChange={(e) => set("edition_alert_text", e.target.value)} placeholder="A new edition ring has just left our atelier…" className={inputCls} data-testid="edition-alert-text-input" /></Field>
      </div>
      {st && (
        <>
          <p className="text-xs text-neutral-500" data-testid="edition-alert-summary">{st.subscribers} subscriber{st.subscribers === 1 ? "" : "s"} · {st.pending.length} new ring{st.pending.length === 1 ? "" : "s"} waiting
            {st.pending.length > 0 && <button onClick={runNow} className="underline ml-2" data-testid="edition-alert-run">Send now</button>}</p>
          <TestSend rings={st.rings} />
          {st.history.length > 0 && (
            <div className="text-xs space-y-1" data-testid="edition-alert-history">
              <p className="eyebrow text-[10px] text-neutral-500">Sent alerts</p>
              {st.history.map((h: any) => <p key={h.id}>{h.name} — {when(h.edition_alerted_at)} · {h.edition_alert_sent ?? "…"} of {h.edition_alert_total ?? "…"} delivered</p>)}
            </div>
          )}
        </>
      )}
    </div>
  );
}
